import React, { useState, useEffect, useCallback } from 'react';
import API_UTIL from '../../../services/AuthIntereptor';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { LazyLoadImage } from "react-lazy-load-image-component";
import Masonry from "react-masonry-css";
import { Heart, Edit2, Upload, X, ChevronUp, ChevronDown } from "lucide-react";
import { FaInstagram, FaYoutube, FaFacebook } from "react-icons/fa";
import { useDropzone } from 'react-dropzone';
import { AnimatePresence } from 'framer-motion';
import AppBar from '../../../components/AppBar/AppBar';
import defaultBanner from '../../../media/images/defaultBanner.jpg';
import Modal from 'react-modal';
import ImageModal from "../../../components/ImageModal/ImageModal";
import { Button, Input } from '../../../components/ui';

const PortfolioForm = () => {
  const userPhoneNumber = localStorage.userPhoneNumber;
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    user_phone_number: userPhoneNumber,
    social_media: {
      instagram: '',
      youtube: '',
      facebook: '',
    },
    org_name: '',
    role: 'creator',
  });
  const [bannerImage, setBannerImage] = useState(defaultBanner);

  const [clickedImg, setClickedImg] = useState(null);
  const [clickedImgIndex, setClickedImgIndex] = useState(null);
  const [clickedImgFavourite, setClickedImgFavourite] = useState(null);
  const [clickedUrl, setClickedUrl] = useState(null);


  const [folders, setFolders] = useState([]);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [userName, setUserName] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [breakpointColumnsObj, setBreakpointColumnsObj] = useState({
    default: 5,
    1200: 4,
    992: 3,
    768: 2,
    576: 1,
  });

  const [uploadStatus, setUploadStatus] = useState('');
  const [uploading, setUploading] = useState(false);
  const [fileCount, setFileCount] = useState(0);
  const [totalUploadedBytes, setTotalUploadedBytes] = useState(0);
  const [isUploadFilesModelOpen, setUploadFilesModeOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFilesModalStatus, setUploadFilesModalStatus] = useState('');
  const [isUploadFilesFailed, setIsUploadFilesFailed] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const [uploadingFolderName, setUploadingFolderName] = useState('');
  const [images, setImages] = useState([]);

  const [expandedFolderIndex, setExpandedFolderIndex] = useState(0);  // First folder expanded by default


  const handleBannerImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('bannerImage', file);
      formData.append('userName', userName);

      try {
        const response = await API_UTIL.post('/updateBannerImage', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data && response.data.imageUrl) {
          const formattedUrl = encodeURIWithPlus(response.data.imageUrl);
          console.log(`formattedUrl:`, formattedUrl);

          setBannerImage(`${formattedUrl}?t=${Date.now()}`);

        }
      } catch (error) {
        console.error('Error uploading banner image:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const totalFiles = acceptedFiles.length;
    if (totalFiles > 500) {
      setFileCount(totalFiles);
      setUploadStatus(`You have selected ${totalFiles} files. You can upload a maximum of 30 files`);
      setUploading(false);
    } else {
      setFiles(acceptedFiles);
      setFileCount(totalFiles);
      setUploadProgress({});
      setUploadStatus('');
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));




  const uploadFiles = async () => {
    if (files.length === 0) {
      setUploadStatus('Please select files to upload');
      return;
    }

    setUploading(true);
    setUploadFilesModalStatus('Uploading files...');
    setIsUploadFilesFailed(false);
    setOverallProgress(0); // Start at 0% progress

    const MAX_CONCURRENT_UPLOADS = 3; // Reduced to avoid 429 error
    const MAX_RETRIES = 3;
    let index = 0;
    const totalFiles = files.length;
    const totalBytes = files.reduce((acc, file) => acc + file.size, 0); // Total size of all files

    // Use React state to safely track the total uploaded bytes

    const uploadFile = async (file) => {
      const data = new FormData();
      data.append('user_phone_number', formData.user_phone_number);
      data.append('user_name', userName);
      data.append(`${uploadingFolderName}_images`, file);

      let attempts = 0;
      let delayTime = 1000; // Start with 1 second delay

      while (attempts < MAX_RETRIES) {
        try {
          const response = await API_UTIL.post('/uploadPortfolioImages', data, {
            headers: {
              'Content-Type': 'multipart/form-data',
            }, onUploadProgress: (progressEvent) => {
              // Dynamically update the total uploaded bytes using state update function
              setTotalUploadedBytes((prevUploadedBytes) => {
                const updatedUploadedBytes = prevUploadedBytes + progressEvent.loaded;

                // Calculate and update overall progress based on total uploaded bytes
                const overallProgress = (updatedUploadedBytes / totalBytes) * 100;
                setOverallProgress(Math.ceil(overallProgress)); // Round to nearest integer

                return updatedUploadedBytes;
              });
            },
          });

          return response.data;
        } catch (error) {
          if (error.response && error.response.status === 429) {
            console.error(`Error uploading file ${file.name}, attempt ${attempts}: Too many requests, retrying after ${delayTime / 1000} seconds`);
            await delay(delayTime);
            delayTime *= 2; // Double the delay time for exponential backoff
          } else {
            console.error(`Error uploading file ${file.name}, attempt ${attempts}:`, error);
          }
          attempts++;
          if (attempts === MAX_RETRIES) throw error;
        }
      }
    };

    const handleUploads = async () => {
      while (index < files.length) {
        const promises = [];
        for (let i = 0; i < MAX_CONCURRENT_UPLOADS && index < files.length; i++) {
          promises.push(uploadFile(files[index]));
          index++;
        }
        await Promise.allSettled(promises);
      }
    };

    try {
      await handleUploads();
      setUploadStatus('Upload completed successfully');
      setFolders(prevFolders => {
        const existingFolderIndex = prevFolders.findIndex(folder => folder.folderName === uploadingFolderName);

        // Map file details as needed
        const newImages = files.map(file => ({
          s3_url: `https://flashbackportfoliouploads.s3.ap-south-1.amazonaws.com/${userName}/${uploadingFolderName}/${file.name}`,
          uploadDate: Date.now(),
          is_favourite: false
        }));

        let updatedFolder;

        if (existingFolderIndex !== -1) {
          // Folder exists, update its images
          updatedFolder = {
            ...prevFolders[existingFolderIndex],
            images: [...prevFolders[existingFolderIndex].images, ...newImages]  // Append new images
          };

          // Remove the existing folder and reinsert at the top
          const remainingFolders = prevFolders.filter((_, index) => index !== existingFolderIndex);
          return [updatedFolder, ...remainingFolders];
        } else {
          // Folder doesn't exist, create a new one and add it to the top
          updatedFolder = {
            folderName: uploadingFolderName,
            images: newImages
          };

          return [updatedFolder, ...prevFolders];
        }
      });
      setExpandedFolderIndex(0);


      setFiles([]);
      console.log(folders);

    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStatus('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setOverallProgress(100); // Ensure progress bar is set to 100% on completion
      setFileCount(0);
      setUploadFilesModeOpen(false);
      setUploadingFolderName('');
    }
  };



  const handleEditClick = () => {
    console.log("Edit Click");
    setEditMode(true);
  }
  const handleCancelEdit = () => setEditMode(false);


  const handleImageClick = (imageData, index, images) => {
    const updatedImages = images.map(image => ({
      original: image.s3_url,  // Rename s3_url to original
      thumbnail: image.s3_url, // Add thumbnail with the same value as s3_url
      isFavourite: image.is_favourite,
    }));

    setImages(updatedImages);
    setClickedImg(imageData.s3_url);
    setClickedImgIndex(index);
    setClickedImgFavourite(imageData.isFavourites);
    setClickedUrl(imageData.original);
    window.history.pushState({ id: 1 }, null, "?image=" + `${imageData.s3_url.split('/').pop()}`);
  };
  const handleBackButton = () => {
    setClickedImg(null);
  };

  const handleFavourite = async (index, imageUrl, isFav) => {
    // Make the API request
    await API_UTIL.post("/setPortfolioFavourites", {
      imageUrl,
      username: formData.user_name,
      isFav,
    });

    // After successful API call, update the folders state
    setFolders(prevFolders =>
      prevFolders.map(folder => {
        const updatedImages = folder.images.map(image =>
          image.s3_url === imageUrl ? { ...image, is_favourite: isFav } : image
        );

        return {
          ...folder,
          images: updatedImages,
        };
      })
    );

    setImages(prevImages =>
      prevImages.map(image =>
        image.original === imageUrl ? { ...image, isFavourite: isFav, isFavourites: isFav } : image
      )
    );
  };



  const toggleFavourite = (index, image) => {
    const isFav = image.is_favourite;
    handleFavourite(index, image.s3_url, !isFav);
  };

  const toggleFolder = (index) => {
    if (index === expandedFolderIndex) {
      setExpandedFolderIndex(null);  // Collapse the currently expanded folder
    } else {
      setExpandedFolderIndex(index);  // Expand the clicked folder
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    console.log(formData);
  };
  const handleFolderName = (e) => {
    setUploadingFolderName(e.target.value);
  }

  const handleFolderNameChange = (index, event) => {
    const { value } = event.target;
    const updatedFolders = [...folders];
    updatedFolders[index].folderName = value;
    setFolders(updatedFolders);
  };

  const addFolder = () => {

    setUploadFilesModeOpen(true);
  };

  const handleSocialMediaChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      social_media: {
        ...prevData.social_media,
        [name]: value,
      },
    }));
  };



  const fetchPortfolioImages = async (user_name) => {
    try {
      console.log('username:', user_name);
      const response = await API_UTIL.get(`/getPortfolioImages/${user_name}`);
      if (response.status === 200) {
        setFolders(response.data);
      }
    } catch (error) {
      console.error('Error fetching portfolio images:', error);
    }
  };

  const fetchUserDetails = async () => {
    try {
      const response = await API_UTIL.get(`/fetchUserDetails/${userPhoneNumber}`);
      setIsLoading(false);

      // Check if the user details have the `org_name` and if the user is the same.
      if (
        userPhoneNumber !== response.data.data.user_name &&
        response.data.data.hasOwnProperty('org_name')
      ) {
        // Update the formData state with the retrieved user details.
        setFormData((prevFormData) => ({
          ...prevFormData,
          org_name: response.data.data.org_name || '',
          social_media: {
            instagram: response.data.data.social_media?.instagram || '',
            youtube: response.data.data.social_media?.youtube || '',
            facebook: response.data.data.social_media?.facebook || '',
          },
          role: response.data.data.role || 'creator',
          user_name: response.data.data.user_name
        }));
        if (!response.data.data.user_name) setEditMode(true);
        else {

          setUserName(response.data.data.user_name);
          fetchBannerImage(response.data.data);
          fetchPortfolioImages(response.data.data.user_name);

        }
      }
      else {
        setEditMode(true);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const fetchBannerImage = useCallback(async (userDetails) => {
    console.log('Entering FetchBannerImage Method and started loading');
    setIsLoading(true);
    if (userDetails.org_name && userDetails.user_name) {
      try {
        const response = await API_UTIL.get(`/getBannerImage/${userDetails.user_name}`);
        console.log(`ImageURl:`, response.data.imageUrl);

        if (response.data && response.data.imageUrl) {
          const formattedUrl = encodeURIWithPlus(response.data.imageUrl);
          console.log(`formattedUrl:`, formattedUrl);
          setBannerImage(`${formattedUrl}?t=${Date.now()}`);
        } else {
          console.log('[catch1]Falling back to default banner');
          setBannerImage(defaultBanner);
        }
      } catch (error) {
        console.error('Error fetching banner image:', error);
        console.log(`[catch2]Falling back to default banner`);
        setBannerImage(defaultBanner);
      } finally {
        setIsLoading(false);
      }
    } else {
      console.log(`[catch3]Falling back to default banner`);
      setBannerImage(defaultBanner);
      setIsLoading(false);
    }
  }, []);


  const encodeURIWithPlus = (uri) => {
    return uri.replace(/ /g, '+');
  };

  useEffect(() => {
    fetchUserDetails();

  }, [navigate]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let requestData = {
        user_phone_number: formData.user_phone_number,
        ...formData,
      };

      // Check if user_name exists and remove it from requestData
      if (!userName) { // Assuming userName is the variable or function checking the existence of the name
        requestData.user_name = formData.org_name; // Add user_name from org_name
      } else { // Assuming you have a function to check if userName exists
        const { user_name, ...rest } = requestData; // Destructure and exclude user_name
        requestData = { ...rest }; // Create a new object without user_name
      }

      const res = await API_UTIL.post("/updatePortfolioDetails", requestData);
      if (res.status === 200) {
        setActiveTab('images');
        setEditMode(false);
        setUserName(res.data.data.user_name);
      }
    } catch (error) {
      console.error("Error in updating the portfolio details:", error);
      if (error.response) {
        toast.error("Error in updating portfolio");
      }
    }
  };

  const openUploadFilesModal = () => {
    setUploadFilesModeOpen(true);
  };

  const closeUploadFilesModal = () => {
    setUploadFilesModeOpen(false);
    setFiles([]);
    setUploadProgress({});
    setUploadStatus('');
    setUploading(false);
    setFileCount(0);
  };


  const toggleHighlight = (folderIndex, imgIndex) => {
    const updatedFolders = [...folders];
    const image = updatedFolders[folderIndex].images[imgIndex];
    image.highlighted = !image.highlighted;
    setFolders(updatedFolders);
  };

  const modalClass =
    "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-md bg-white rounded-2xl shadow-card p-6 outline-none z-[1000] max-h-[85vh] overflow-y-auto";
  const overlayClass = "fixed inset-0 bg-black/60 z-[999]";

  return (
    <div className="min-h-screen bg-gray-50">
      <AppBar showCoins={true} />
      <div className="fade-in max-w-[1400px] mx-auto px-4 sm:px-6 py-6 flex flex-col lg:flex-row gap-6">
        {/* Side panel */}
        <aside className="lg:w-[320px] lg:shrink-0">
          <div className="lg:sticky lg:top-20 bg-white border border-gray-200 rounded-2xl shadow-card p-5">
            <div className="relative aspect-[5/3] rounded-xl overflow-hidden border border-gray-200 mb-4">
              <img src={bannerImage} alt="Event" className="w-full h-full object-cover" />
              <button
                onClick={handleEditClick}
                aria-label="Edit portfolio"
                title="Edit portfolio"
                className="absolute top-2 right-2 w-9 h-9 grid place-items-center bg-white/95 backdrop-blur border border-gray-200 hover:border-brand-200 hover:text-brand-600 text-slate-600 rounded-lg shadow-soft"
              >
                <Edit2 size={16} />
              </button>
            </div>

            <h1 className="text-xl font-bold text-slate-900 mb-3">{formData.org_name}</h1>

            <div className="space-y-2 text-sm text-slate-600 mb-5">
              <div className="flex items-center gap-2">
                <FaInstagram size={16} className="text-brand-600 shrink-0" />
                <span className="truncate">{formData.social_media.instagram || 'Link not set'}</span>
              </div>
              <div className="flex items-center gap-2">
                <FaYoutube size={16} className="text-brand-600 shrink-0" />
                <span className="truncate">{formData.social_media.youtube || 'Link not set'}</span>
              </div>
              <div className="flex items-center gap-2">
                <FaFacebook size={16} className="text-brand-600 shrink-0" />
                <span className="truncate">{formData.social_media.facebook || 'Link not set'}</span>
              </div>
            </div>

            <Button type="button" onClick={addFolder} icon={Upload} className="w-full">
              Add Folder
            </Button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          <AnimatePresence>
            {folders.map((folder, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl shadow-card mb-5 overflow-hidden">
                <div
                  className="flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50 transition"
                  onClick={() => toggleFolder(index)}
                >
                  <span className="font-semibold text-slate-900">{folder.folderName}</span>
                  <span className="text-slate-500">
                    {expandedFolderIndex === index ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </span>
                </div>

                {expandedFolderIndex === index && (
                  <div className="px-4 pb-4">
                    <Masonry
                      breakpointCols={breakpointColumnsObj}
                      className="flex -ml-3 w-auto"
                      columnClassName="pl-3 bg-clip-padding"
                    >
                      <div
                        onClick={() => { openUploadFilesModal(); setUploadingFolderName(folder.folderName); }}
                        className="mb-3 rounded-xl border-2 border-dashed border-gray-300 hover:border-brand-500 hover:bg-brand-50/30 transition h-[150px] grid place-items-center text-slate-500 cursor-pointer"
                      >
                        <div className="text-center">
                          <Upload size={22} className="mx-auto text-brand-600" />
                          <span className="block text-xs font-semibold mt-2">Upload images</span>
                        </div>
                      </div>

                      {folder.images.map((image, imgIndex) => (
                        <div
                          key={imgIndex}
                          className="relative mb-3 rounded-xl overflow-hidden border border-gray-200 group cursor-pointer"
                        >
                          <LazyLoadImage
                            src={image.s3_url}
                            alt={`img ${index}`}
                            effect="blur"
                            className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-105"
                            onClick={() => handleImageClick(image, imgIndex, folder.images)}
                          />
                          <button
                            type="button"
                            aria-label={image.is_favourite ? "Unfavourite" : "Favourite"}
                            className="absolute bottom-2 right-2 w-8 h-8 grid place-items-center bg-white/95 backdrop-blur rounded-full border border-gray-200 shadow-soft opacity-90 group-hover:opacity-100 transition"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavourite(index, image);
                            }}
                          >
                            <Heart
                              size={16}
                              className={image.is_favourite ? "text-red-500 fill-red-500" : "text-slate-500"}
                            />
                          </button>
                        </div>
                      ))}
                    </Masonry>
                  </div>
                )}
              </div>
            ))}
          </AnimatePresence>
        </main>
      </div>

      {clickedImg && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/90">
          <ImageModal
            clickedImg={clickedImg}
            clickedImgIndex={clickedImgIndex}
            clickedImgFavourite={clickedImgFavourite}
            setClickedImg={setClickedImg}
            clickedUrl={clickedUrl}
            handleBackButton={handleBackButton}
            handleFavourite={handleFavourite}
            images={images}
          />
        </div>
      )}

      {/* Upload files modal */}
      <Modal
        isOpen={isUploadFilesModelOpen}
        onRequestClose={closeUploadFilesModal}
        contentLabel="Upload Files"
        className={modalClass}
        overlayClassName={overlayClass}
      >
        <button
          onClick={closeUploadFilesModal}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 grid place-items-center rounded-lg text-slate-500 hover:bg-gray-100 hover:text-slate-800 transition"
        >
          <X size={20} />
        </button>

        <h2 className="text-lg font-bold text-slate-900 mb-4">Upload Files</h2>

        <Input
          label="Folder Name"
          type="text"
          id="folderName"
          name="folderName"
          value={uploadingFolderName}
          onChange={handleFolderName}
          required
        />

        <div
          {...getRootProps()}
          className={`mt-4 rounded-xl border-2 border-dashed px-4 py-8 text-center cursor-pointer transition ${
            isDragActive ? "border-brand-500 bg-brand-50/40" : "border-gray-300 hover:border-brand-500 hover:bg-brand-50/20"
          }`}
        >
          <input {...getInputProps()} />
          <Upload size={22} className="mx-auto text-brand-600 mb-2" />
          {files.length > 0 ? (
            <p className="text-sm text-slate-600">{fileCount} file(s) selected.</p>
          ) : (
            <p className="text-sm text-slate-500">Drag 'n' drop files here, or click to select files</p>
          )}
        </div>

        {uploadStatus && <p className="mt-3 text-sm text-slate-500 text-center">{uploadStatus}</p>}

        <div className="flex justify-center pt-4">
          <Button onClick={uploadFiles} disabled={files.length === 0 || fileCount > 30}>
            Upload
          </Button>
        </div>

        {uploading && (
          <div className="mt-4 w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-600 transition-all"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        )}
      </Modal>

      {/* Edit / create portfolio modal */}
      <AnimatePresence>
        {editMode && (
          <Modal
            isOpen={editMode}
            onRequestClose={handleCancelEdit}
            contentLabel="Edit Event"
            className={modalClass}
            overlayClassName={overlayClass}
          >
            <button
              onClick={handleCancelEdit}
              aria-label="Close"
              className="absolute top-3 right-3 w-9 h-9 grid place-items-center rounded-lg text-slate-500 hover:bg-gray-100 hover:text-slate-800 transition"
            >
              <X size={20} />
            </button>

            <h2 className="text-lg font-bold text-slate-900 mb-4">Create Portfolio</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Brand Name"
                type="text"
                id="org_name"
                name="org_name"
                placeholder="Enter your Brand Name"
                value={formData.org_name}
                onChange={handleInputChange}
                required
              />
              <Input
                label="Instagram URL"
                type="text"
                id="instagram"
                name="instagram"
                placeholder="Provide your Instagram URL"
                value={formData.social_media.instagram}
                onChange={handleSocialMediaChange}
              />
              <Input
                label="YouTube URL"
                type="text"
                id="youtube"
                name="youtube"
                placeholder="Provide your YouTube URL"
                value={formData.social_media.youtube}
                onChange={handleSocialMediaChange}
              />
              <Input
                label="Facebook URL"
                type="text"
                id="facebook"
                name="facebook"
                placeholder="Provide your Facebook URL"
                value={formData.social_media.facebook}
                onChange={handleSocialMediaChange}
              />
              <div>
                <span className="block text-xs font-semibold text-slate-700 mb-1.5">Upload Banner Image</span>
                <input
                  type="file"
                  id="eventImage"
                  name="eventImage"
                  accept="image/*"
                  onChange={handleBannerImageUpload}
                  className="block w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border file:border-gray-200 file:bg-white file:text-sm file:font-medium file:text-slate-700 hover:file:bg-gray-50"
                />
              </div>
              <Button type="submit" className="w-full">Save</Button>
            </form>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PortfolioForm;
